import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useExpenseList } from "../hooks/useExpenses";
import { NewExpenseDrawer } from "./NewExpenseDrawer";
import { formatCurrency } from "./CurrencyAmount";

const BORDER = "hsl(210,30%,85%)";

type Props = {
  companyId?: string;
  productId?: string;
  /** When productId is set and this is true, include expenses linked via a
   *  project that belongs to that product (in addition to direct links). */
  productIncludeProjectLinks?: boolean;
  projectId?: string;
  budgetId?: string;
  meetingId?: string;
  accessVaultId?: string;
  assetId?: string;
  vendorContactId?: string;
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string }> = {
    pending: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" },
    approved: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
    rejected: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)" },
  };
  const s = styles[status] ?? styles.pending;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
      style={{ background: s.bg, color: s.fg }}
    >
      {status}
    </span>
  );
}

export function ExpensesTabContent({
  companyId,
  productId,
  productIncludeProjectLinks,
  projectId,
  budgetId,
  meetingId,
  accessVaultId,
  assetId,
  vendorContactId,
}: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useExpenseList({
    companyId,
    productId,
    productIncludeProjectLinks,
    projectId,
    budgetId,
    meetingId,
    accessVaultId,
    assetId,
    vendorContactId,
    limit: 100,
  });
  const expenses = data?.data ?? [];
  const summary = data?.summary;

  // Lifetime-cost summary header for the entity-scoped variants (vault, asset,
  // vendor contact, or product-includes-projects). These views want a single
  // headline number plus last-spend date alongside the per-status breakdown.
  const isEntityScope =
    !!accessVaultId ||
    !!assetId ||
    !!vendorContactId ||
    (!!productId && !!productIncludeProjectLinks);
  const totalSpent = expenses.reduce((sum, e) => sum + e.baseAmount, 0);
  const lastExpenseDate = expenses[0]?.expenseDate ?? null;

  return (
    <div className="space-y-3">
      {isEntityScope && summary && summary.totalCount > 0 && (
        <div
          className="rounded-md border px-4 py-2 text-[13px]"
          style={{
            borderColor: "hsl(210,55%,80%)",
            background: "hsl(210,55%,96%)",
          }}
        >
          <span className="text-foreground">
            Total spent:{" "}
            <span className="font-semibold">
              {formatCurrency(totalSpent, "AED")}
            </span>{" "}
            <span className="text-muted-foreground">
              • {summary.totalCount} expense{summary.totalCount === 1 ? "" : "s"}
            </span>
            {lastExpenseDate && (
              <span className="text-muted-foreground">
                {" "}
                • Last expense: {formatDate(lastExpenseDate)}
              </span>
            )}
          </span>
        </div>
      )}
      {summary && summary.totalCount > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Pending" value={formatCurrency(summary.pendingAmount, "AED")} color="hsl(38,92%,30%)" />
          <Stat label="Approved" value={formatCurrency(summary.approvedAmount, "AED")} color="hsl(142,72%,28%)" />
          <Stat label="Total Count" value={String(summary.totalCount)} />
          <Stat label="Rejected Count" value={String(summary.rejectedCount)} color="hsl(0,70%,45%)" />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {expenses.length} {expenses.length === 1 ? "expense" : "expenses"}
        </div>
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          Add Expense
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : expenses.length === 0 ? (
        <div
          className="rounded-xl border bg-white py-12 text-center"
          style={{ borderColor: BORDER }}
        >
          <Receipt
            className="mx-auto h-8 w-8 text-muted-foreground"
            strokeWidth={1.5}
          />
          <div className="mt-2 text-sm text-muted-foreground">
            No expenses yet
          </div>
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
            <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-[14px] py-[9px]" style={{ width: 110 }}>#</th>
                <th className="px-[14px] py-[9px]">Title</th>
                {productIncludeProjectLinks && (
                  <th className="px-[14px] py-[9px]" style={{ width: 160 }}>
                    Link Type
                  </th>
                )}
                <th className="px-[14px] py-[9px]" style={{ width: 120 }}>Category</th>
                <th className="px-[14px] py-[9px]" style={{ width: 130 }}>Amount</th>
                <th className="px-[14px] py-[9px]" style={{ width: 130 }}>Base (AED)</th>
                <th className="px-[14px] py-[9px]" style={{ width: 110 }}>Status</th>
                <th className="px-[14px] py-[9px]" style={{ width: 120 }}>Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {expenses.map((e) => (
                <tr
                  key={e.id}
                  className="cursor-pointer hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/expenses/${e.id}`)}
                >
                  <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                    {e.code}
                  </td>
                  <td className="px-[14px] py-[11px] font-medium text-foreground">
                    {e.title}
                  </td>
                  {productIncludeProjectLinks && (
                    <td className="px-[14px] py-[11px] text-[12px] text-muted-foreground">
                      {e.productId === productId ? (
                        <span className="rounded-full bg-[hsl(210,55%,92%)] px-2 py-0.5 text-[10px] font-medium text-[hsl(210,100%,28%)]">
                          Direct
                        </span>
                      ) : e.projectName ? (
                        <span title={`via project: ${e.projectName}`}>
                          via {e.projectName}
                        </span>
                      ) : (
                        "via Project"
                      )}
                    </td>
                  )}
                  <td className="px-[14px] py-[11px] text-muted-foreground capitalize">
                    {e.category?.replace(/_/g, " ") ?? "—"}
                  </td>
                  <td className="px-[14px] py-[11px] text-foreground">
                    {formatCurrency(e.amount, e.currency)}
                  </td>
                  <td className="px-[14px] py-[11px] text-foreground">
                    {formatCurrency(e.baseAmount, e.baseCurrency)}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <StatusPill status={e.status} />
                  </td>
                  <td className="px-[14px] py-[11px] text-foreground">
                    {formatDate(e.expenseDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewExpenseDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        prefill={{
          companyId,
          productId,
          projectId,
          budgetId,
          meetingId,
          accessVaultId,
          assetId,
          vendorContactId,
          locked: {
            companyId: !!companyId && !projectId && !productId,
            productId: !!productId && !projectId,
            projectId: !!projectId,
            budgetId: !!budgetId,
            meetingId: !!meetingId,
            accessVaultId: !!accessVaultId,
            assetId: !!assetId,
            vendorContactId: !!vendorContactId,
          },
        }}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div
      className="rounded-lg border bg-white px-3 py-2"
      style={{ borderColor: "hsl(210,30%,90%)" }}
    >
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className="mt-0.5 text-[14px] font-semibold"
        style={{ color: color ?? "hsl(215,20%,25%)" }}
      >
        {value}
      </div>
    </div>
  );
}
